/**
 * prevNamePath - always required if adding a new component option
 */

import { componentNames, pathNames } from "../../common/shared";
import { IPathConfiguration } from "./models/interfaces/IPathConfigurationProps";

export const initialPathConfig: IPathConfiguration[] = [
    {
        namePath: pathNames.INITIAL_PATH,
        componentName: componentNames.HELP_WITH_MY_ORDER,
        stepConfig: {
            calculateStepNum: false,
            title: 'Report a delivery issue',
        },
    },
    {
        namePath: pathNames.UNSURE_ORDER_STATUS,
        metrics: ['', 'tool_unsureorderstatus_step2submit'],
        componentName: componentNames.USER_DATA,
        stepConfig: {
            step: 2,
            totalStep: 2,
            nextNamePath: pathNames.REQUEST_SEND,
            prevNamePath: pathNames.INITIAL_PATH,
            title: 'Unsure of order status',
            emailInput: { show: true, required: true },
            orderNumberInput: { show: true, required: true, maxLength: 10 },
            descriptionInput: {
                show: true,
                required: true,
                label: 'Describe the issue',
                labelToSend: 'Describe the issue:',
            },
            showSubmit: true,
        },
    },
    {
        namePath: pathNames.TRACKING_SHOW_BUT_NOT_HERE,
        metrics: ['', 'tool_trackingshowbutnothere_step2submit', 'tool_trackingshowbutnothere_step3submit', 'tool_trackingshowbutnothere_step4submit'],
        componentName: componentNames.INFORMATION_PAGE,
        stepConfig: {
            step: 2,
            totalStep: 4,
            nextNamePath: pathNames.TRACKING_SHOW_BUT_NOT_HERE_USER_DATA,
            prevNamePath: pathNames.INITIAL_PATH,
            title: 'Tracking shows delivered but it’s not here',
        },
    },
    {
        namePath: pathNames.TRACKING_SHOW_BUT_NOT_HERE_USER_DATA,
        componentName: componentNames.USER_DATA,
        stepConfig: {
            calculateStepNum: true,
            nextNamePath: pathNames.HOW_CAN_HELP,
            prevNamePath: pathNames.TRACKING_SHOW_BUT_NOT_HERE,
            title: 'Tracking shows delivered but it’s not here',
            emailInput: { show: true, required: true },
            orderNumberInput: { show: true, required: true, maxLength: 10 },
            descriptionInput: {
                show: true,
                required: true,
                label: 'Describe the issue',
                labelToSend: 'Describe the issue:',
            },
        },
    },
    {
        namePath: pathNames.LOST_ORDER,
        metrics: ['', 'tool_lost/missingorder_step2submit', 'tool_lost/missingorder_step3submit', 'tool_lost/missingorder_step4submit'],
        componentName: componentNames.INFORMATION_PAGE,
        stepConfig: {
            step: 2,
            totalStep: 4,
            nextNamePath: pathNames.LOST_ORDER_USER_DATA,
            prevNamePath: pathNames.INITIAL_PATH,
            title: 'Lost order',
        },
    },
    {
        namePath: pathNames.LOST_ORDER_USER_DATA,
        componentName: componentNames.USER_DATA,
        stepConfig: {
            calculateStepNum: true,
            nextNamePath: pathNames.HOW_CAN_HELP,
            prevNamePath: pathNames.LOST_ORDER,
            title: 'Lost order',
            emailInput: { show: true, required: true },
            orderNumberInput: { show: true, required: true, maxLength: 10 },
            descriptionInput: {
                show: true,
                required: true,
                label: 'Describe the issue',
                labelToSend: 'Describe the issue:',
            },
        },
    },
    {
        namePath: pathNames.MISSING_ITEM,
        metrics: ['', 'tool_missingitem_step2submit', 'tool_missingitem_step3submit', 'tool_missingitem_step4submit'],
        componentName: componentNames.INFORMATION_PAGE,
        stepConfig: {
            step: 2,
            totalStep: 4,
            nextNamePath: pathNames.MISSING_ITEM_USER_DATA,
            prevNamePath: pathNames.INITIAL_PATH,
            title: 'Missing item',
        },
    },
    {
        namePath: pathNames.MISSING_ITEM_USER_DATA,
        componentName: componentNames.USER_DATA,
        stepConfig: {
            calculateStepNum: true,
            nextNamePath: pathNames.HOW_CAN_HELP,
            prevNamePath: pathNames.MISSING_ITEM,
            title: 'Missing item',
            emailInput: { show: true, required: true },
            orderNumberInput: { show: true, required: true, maxLength: 10 },
            itemInput: {
                show: true,
                required: true,
                label: 'What item(s) are missing?',
                labelToSend: 'What item(s) are missing?:',
                showItemDescriptionDetailsLabel: true,
            },
            showItemsDintOrder: true,
            itemDintOrderInput: {
                required: true,
                label: 'What did you receive?',
                labelToSend: 'What did you receive?:',
            },
        },
    },
    {
        namePath: pathNames.INCORRECT_ITEM,
        metrics: ['', 'tool_incorrectitem_step2submit', 'tool_incorrectitem_step3submit', 'tool_incorrectitem_step4submit'],
        componentName: componentNames.WRONG_ORDER_INFO,
        stepConfig: {
            step: 2,
            totalStep: 4,
            nextNamePath: pathNames.INCORRECT_ITEM_USER_DATA,
            prevNamePath: pathNames.INITIAL_PATH,
            title: 'Received incorrect item',
        },
    },
    {
        namePath: pathNames.INCORRECT_ITEM_USER_DATA,
        componentName: componentNames.USER_DATA,
        stepConfig: {
            step: 3,
            totalStep: 4,
            nextNamePath: pathNames.HOW_CAN_HELP,
            prevNamePath: pathNames.INCORRECT_ITEM,
            title: 'Received incorrect item',
            emailInput: { show: true, required: true },
            orderNumberInput: { show: true, required: true, maxLength: 10 },
            itemInput: {
                show: true,
                required: true,
                label: 'What item(s) did you originally buy?',
                labelToSend: 'What item(s) did you originally buy?:',
                showItemDescriptionDetailsLabel: true,
            },
            descriptionInput: {
                show: true,
                required: true,
                label: 'What item(s) did you get instead?',
                labelToSend: 'What item(s) did you get instead?:',
                showItemDescriptionDetailsLabel: true,
            },
        },
    },
    {
        namePath: pathNames.RECEIVED_DAMAGED,
        metrics: ['', 'tool_damageditem_step2submit', 'tool_damageditem_step3submit'],
        componentName: componentNames.USER_DATA,
        stepConfig: {
            urlKey: 'damaged-item',
        },
    },
    {
        namePath: pathNames.SOMETHING_ELSE,
        metrics: ['', 'tool_somethingelse_step2submit'],
        componentName: componentNames.USER_DATA,
        stepConfig: {
            step: 2,
            totalStep: 2,
            nextNamePath: pathNames.REQUEST_SEND,
            prevNamePath: pathNames.INITIAL_PATH,
            title: 'Something else',
            emailInput: { show: true, required: true },
            orderNumberInput: { show: true, required: true, maxLength: 10 },
            descriptionInput: {
                show: true, required: true, label: 'Describe the issue', labelToSend: 'Describe the issue:',
            },
            showSubmit: true,
        },
    },
    {
        namePath: pathNames.HOW_CAN_HELP,
        componentName: componentNames.HOW_CAN_WE_HELP,
        stepConfig: {
            calculateStepNum: true,
            nextNamePath: pathNames.REQUEST_SEND,
            title: 'How can we help?',
            shippingInput: { show: true, required: true, label: 'What’s your shipping address?' },
            showSubmit: true,
        },
    },
    {
        namePath: pathNames.REQUEST_SEND,
        componentName: componentNames.REQUEST_SEND,
        stepConfig: {
            calculateStepNum: true,
            title: 'Help request sent',
        },
    },
];
